/**
 * Google Publisher Tag (GPT) utilities for Google Ad Manager integration
 * Based on DraftKings Tagging Architecture requirements
 */

import type { ResponsiveSizeMapping } from '@/config/ads.config';

// Extend Window interface for GPT
declare global {
  interface Window {
    googletag: any;
  }
}

export const initializeGPT = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if GPT is already loaded and ready
    if (window.googletag && window.googletag.apiReady) {
      console.log('[GPT] Already initialized');
      resolve();
      return;
    }

    // Ensure googletag object exists (should be created by script in HEAD)
    window.googletag = window.googletag || { cmd: [] };

    // Wait for the script loaded from HEAD to become ready
    // The script in HEAD creates the googletag object and loads gpt.js
    let attempts = 0;
    const maxAttempts = 200; // 10 seconds max (200 * 50ms)

    const checkGPT = () => {
      attempts++;

      // Check if GPT API is ready
      if (window.googletag && window.googletag.apiReady) {
        console.log('[GPT] Script loaded from HEAD, initialized');
        resolve();
        return;
      }

      // Check if we've exceeded max attempts
      if (attempts >= maxAttempts) {
        console.error('[GPT] Timeout waiting for script to load from HEAD');
        reject(new Error('Timeout waiting for GPT script to load'));
        return;
      }

      // Check again after a short delay
      setTimeout(checkGPT, 50);
    };

    // Start checking after a short delay to allow script to start loading
    setTimeout(checkGPT, 100);
  });
};

/**
 * Page-level targeting parameters
 * Based on DraftKings Tagging Architecture key-value targeting
 */
export interface PageTargeting {
  // DraftKings standard targeting keys
  s1?: string; // Sport (e.g., 'nfl', 'nba')
  s2?: string; // Game type (e.g., 'pickem', 'dfs')
  s3?: string; // Page type (e.g., 'lobby', 'contest', 'leaderboard')
  s5?: string; // Contest Id
  pos?: string; // Position (e.g., 'top', 'bottom')
  pid?: string; // Player/User ID
  contestid?: string; // Contest ID
  test?: string; // Test mode indicator ('true' or 'false')

  // Legacy keys for backward compatibility
  gameType?: string;
  sport?: string;
  contestId?: string;
  userId?: string;
}

/**
 * Set page-level targeting parameters
 * Supports both DraftKings standard keys (s1, s2, s3, s5, pos, pid)
 * and legacy keys for backward compatibility
 */
export const setPageTargeting = (targeting: PageTargeting): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    const pubads = window.googletag.pubads();

    // Dynamically set all targeting parameters
    Object.entries(targeting).forEach(([key, value]) => {
      if (value) {
        pubads.setTargeting(key, value);
      }
    });

    console.log('[GPT] Page-level targeting set:', targeting);
  });
};

/**
 * Clear a single page-level targeting key
 * Used to avoid a stale value (e.g. a contest ID) leaking onto subsequent
 * pages after navigating away from the page that set it.
 */
export const clearPageTargeting = (key: keyof PageTargeting): void => {
  if (!window.googletag) {
    return;
  }

  window.googletag.cmd.push(() => {
    window.googletag.pubads().clearTargeting(key);
    console.log('[GPT] Cleared page-level targeting key:', key);
  });
};

/**
 * Ad slot configuration
 * Supports responsive size mappings based on DraftKings Tagging Architecture
 */
export interface AdSlotConfig {
  adUnitPath: string;
  sizes: [number, number][];
  divId: string;
  targeting?: Record<string, string>;
  sizeMappings?: ResponsiveSizeMapping[]; // Responsive size mappings
}

/**
 * Build responsive size mapping for GPT
 * Converts our ResponsiveSizeMapping format to googletag.SizeMapping format
 */
export const buildSizeMapping = (sizeMappings: ResponsiveSizeMapping[]): any => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return null;
  }

  const mapping = window.googletag.sizeMapping();

  // Sort mappings by viewport width (largest first) for proper fallback
  const sortedMappings = [...sizeMappings].sort((a, b) => b.viewport[0] - a.viewport[0]);

  sortedMappings.forEach(({ viewport, sizes }) => {
    mapping.addSize(viewport, sizes);
  });

  return mapping.build();
};

/**
 * Define an ad slot with optional responsive size mappings
 * Based on DraftKings GPT integration pattern:
 * 1. Define slot with sizes
 * 2. Apply size mapping if provided
 * 3. Add service
 * 4. Apply slot-level targeting
 *
 * Note: Page-level targeting should be set separately using setPageTargeting()
 * before or after defining slots (both work, but setting before is recommended)
 */
export const defineAdSlot = (config: AdSlotConfig): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    const slot = window.googletag.defineSlot(config.adUnitPath, config.sizes, config.divId);

    if (config.sizeMappings && config.sizeMappings.length > 0) {
      const mapping = buildSizeMapping(config.sizeMappings);
      if (mapping) {
        slot.defineSizeMapping(mapping);
        console.log('[GPT] Applied responsive size mapping to:', config.divId);
      }
    }

    slot.addService(window.googletag.pubads());

    if (config.targeting) {
      Object.entries(config.targeting).forEach(([key, value]) => {
        slot.setTargeting(key, value);
      });
      console.log('[GPT] Applied slot-level targeting to:', config.divId, config.targeting);
    }

    console.log('[GPT] Defined ad slot:', config.divId, 'with path:', config.adUnitPath);
  });
};

export const enableAndDisplayAds = (): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }

  window.googletag.cmd.push(() => {
    window.googletag.pubads().enableSingleRequest();
    window.googletag.pubads().collapseEmptyDivs();
    window.googletag.enableServices();
    console.log('[GPT] Services enabled');
  });
};

/**
 * Display a specific ad slot
 * This triggers the actual rendering of the ad into the div
 */
export const displayAdSlot = (divId: string): void => {
  if (!window.googletag) {
    console.warn('[GPT] googletag not loaded yet');
    return;
  }
  window.googletag.cmd.push(() => {
    window.googletag.display(divId);
    console.log('[GPT] Displaying ad:', divId);
  });
};

/**
 * A single slot's `slotRenderEnded` event, narrowed to the fields we use.
 * `isEmpty` is GAM's answer to "did anything get sold for this slot?" — it is
 * the only reliable signal that the reserved space should be released.
 */
export interface SlotRenderEndedEvent {
  slot?: {
    getSlotElementId: () => string;
  };
  isEmpty: boolean;
}

/**
 * Subscribe to one slot's render outcome. Returns an unsubscribe function.
 *
 * Both the subscribe and the unsubscribe go through `googletag.cmd`, which is the
 * only safe way to touch pubads: the queue is drained once GPT is live, so a caller
 * that mounts before the library loads still gets its listener attached — and one
 * that unmounts in between still gets it removed, in order.
 */
export const onSlotRenderEnded = (
  divId: string,
  handler: (isEmpty: boolean) => void
): (() => void) => {
  if (!window.googletag) return () => {};

  const listener = (event: SlotRenderEndedEvent) => {
    if (event.slot?.getSlotElementId() === divId) handler(event.isEmpty);
  };

  window.googletag.cmd.push(() =>
    window.googletag.pubads().addEventListener('slotRenderEnded', listener)
  );

  return () => {
    if (!window.googletag) return;
    window.googletag.cmd.push(() =>
      window.googletag.pubads().removeEventListener('slotRenderEnded', listener)
    );
  };
};

/**
 * Destroy an ad slot (cleanup)
 */
export const destroyAdSlot = (divId: string): void => {
  if (!window.googletag) {
    return;
  }

  window.googletag.cmd.push(() => {
    const slots = window.googletag.pubads().getSlots();
    const slot = slots.find((s: any) => s.getSlotElementId() === divId);
    if (slot) {
      window.googletag.destroySlots([slot]);
      console.log('[GPT] Destroyed ad slot:', divId);
    }
  });
};

/**
 * Refresh specific ad slots
 * Used for manual refresh or as part of automatic refresh cycle
 */
export const refreshAdSlots = (divIds: string[]): void => {
  if (!window.googletag) {
    return;
  }

  window.googletag.cmd.push(() => {
    const slots = window.googletag.pubads().getSlots();
    const slotsToRefresh = slots.filter((s: any) => divIds.includes(s.getSlotElementId()));

    if (slotsToRefresh.length > 0) {
      window.googletag.pubads().refresh(slotsToRefresh);
      console.log('[GPT] Refreshed ad slots:', divIds);
    }
  });
};

/**
 * Ad refresh configuration
 * Based on DraftKings spec: 30-60 second intervals
 */
export interface AdRefreshConfig {
  divIds: string[]; // Ad slots to refresh
  intervalSeconds: number; // Refresh interval (30-60 recommended)
  maxRefreshes?: number; // Optional: Maximum number of refreshes (0 = unlimited)
}

/**
 * Start automatic ad refresh for specified slots
 * Returns a cleanup function to stop the refresh cycle
 *
 * @param config - Refresh configuration with divIds, interval, and optional max refreshes
 * @returns Cleanup function to stop refresh cycle
 *
 * @example
 * // Refresh bottom banner every 45 seconds
 * const stopRefresh = startAdRefresh({
 *   divIds: ['sticky-banner-footer-0'],
 *   intervalSeconds: 45,
 *   maxRefreshes: 10 // Stop after 10 refreshes
 * });
 *
 * // Stop refresh when component unmounts
 * return () => stopRefresh();
 */
export const startAdRefresh = (config: AdRefreshConfig): (() => void) => {
  let refreshCount = 0;
  const { divIds, intervalSeconds, maxRefreshes = 0 } = config;

  console.log(
    `[GPT] Starting ad refresh for ${divIds.length} slot(s) every ${intervalSeconds}s` +
      (maxRefreshes > 0 ? ` (max ${maxRefreshes} refreshes)` : '')
  );

  const intervalId = setInterval(() => {
    // Check if we've reached max refreshes
    if (maxRefreshes > 0 && refreshCount >= maxRefreshes) {
      console.log(`[GPT] Reached max refresh count (${maxRefreshes}), stopping refresh`);
      clearInterval(intervalId);
      return;
    }

    refreshAdSlots(divIds);
    refreshCount++;
  }, intervalSeconds * 1000);

  return () => {
    console.log(
      `[GPT] Stopping ad refresh for ${divIds.length} slot(s) (refreshed ${refreshCount} times)`
    );
    clearInterval(intervalId);
  };
};
